/**
 * SSOT: flatten findExtendedDeals / deal.extended SOAP shapes into room rows.
 * Mirrors BE _unwrap_find_extended_deals_list / _coerce_extended_branch_to_room_dicts.
 */

import {
  isHrInternalBase64JsonDealToken,
  isLdsOpaqueDealToken,
  preserveLdsDealIdOnExtendedRow,
} from "./ldsDealTokenSsot";

const EXTENDED_BRANCH_KEYS = [
  "item",
  "items",
  "Item",
  "Items",
  "deal",
  "Deal",
  "row",
  "Row",
  "room",
  "Room",
  "extendedDeal",
  "extendedDeals",
  "ExtendedDeal",
  "ExtendedDeals",
  "extended_deal",
  "deals",
  "Deals",
];

function pickStr(row, keys) {
  if (!row || typeof row !== "object") return "";
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/** @param {unknown} row */
export function looksLikeLdsExtendedRoomRow(row) {
  if (!row || typeof row !== "object") return false;
  if (pickStr(row, ["roomSet", "RoomSet", "room_set"])) return true;
  const name = pickStr(row, [
    "roomTypeName",
    "RoomTypeName",
    "roomName",
    "RoomName",
    "roomType",
    "RoomType",
    "boxName",
    "BoxName",
  ]);
  if (!name) return false;
  const tf = row.totalFare ?? row.TotalFare;
  const af = row.adultFare ?? row.AdultFare;
  return tf != null || af != null;
}

/**
 * @param {unknown} branch
 * @param {number} depth
 * @returns {object[]}
 */
function coerceExtendedBranch(branch, depth = 0) {
  if (depth > 14 || branch == null) return [];

  if (Array.isArray(branch)) {
    return branch.flatMap((el) => coerceExtendedBranch(el, depth + 1));
  }

  if (typeof branch !== "object") return [];

  if (looksLikeLdsExtendedRoomRow(branch)) {
    return [branch];
  }

  let out = [];
  for (const key of EXTENDED_BRANCH_KEYS) {
    if (!(key in branch)) continue;
    out = out.concat(coerceExtendedBranch(branch[key], depth + 1));
    if (out.length) return out;
  }

  const fullDeal = branch.fullDeal ?? branch.FullDeal;
  if (fullDeal && typeof fullDeal === "object") {
    const sub = coerceExtendedBranch(fullDeal, depth + 1);
    if (sub.length) return sub;
  }

  return out;
}

function dedupeExtendedRoomRows(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const seq = pickStr(row, ["@seqNum", "seqNum", "roomId", "RoomId"]);
    const roomSet = pickStr(row, ["roomSet", "RoomSet", "room_set"]);
    const roomType = pickStr(row, [
      "roomType",
      "roomTypeName",
      "roomName",
      "RoomTypeName",
    ]);
    const opaqueTok = pickStr(row, ["ldsDealId", "LdsDealId", "dealToken"]);
    const tok =
      opaqueTok && isLdsOpaqueDealToken(opaqueTok)
        ? opaqueTok
        : isHrInternalBase64JsonDealToken(opaqueTok)
          ? ""
          : opaqueTok;
    const key = `${seq}|${roomSet}|${roomType}|${tok.slice(0, 48)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

/**
 * @param {unknown} value
 * @returns {object[]}
 */
export function normalizeLdsExtendedDealRows(value) {
  if (value == null) return [];
  const rows = coerceExtendedBranch(value)
    .filter(looksLikeLdsExtendedRoomRow)
    .map(preserveLdsDealIdOnExtendedRow);
  return dedupeExtendedRoomRows(rows);
}

/**
 * @param {import("../../services/fhpService").findExtendedDeals|{ data?: object }|object|null|undefined} response
 * @returns {object[]}
 */
export function normalizeLdsExtendedDealRowsFromFindResponse(response) {
  const data = response?.data ?? response;
  const raw =
    data?.extendedDeal ??
    data?.extended_deal ??
    data?.extendedDeals ??
    data?.ExtendedDeal ??
    data?.ExtendedDeals ??
    data;
  return normalizeLdsExtendedDealRows(raw);
}

/**
 * Ingress helper for PackagesProfile / SelectedResultContext.
 * @param {unknown} res — findExtendedDeals axios response or { data }
 * @returns {object[]}
 */
export function extendedDealRowsFromFindResponse(res) {
  return normalizeLdsExtendedDealRowsFromFindResponse(res);
}
